// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: the calendar's reminder banner (calendar-plan.md sections 3, 10 and 12): an accent bar with a bell,
// "<title> today at <time> · in <relative>", "Open calendar" (on that event's day), "+<n> more" when several are due
// (the agenda) and ✕ "Dismiss on this device". Exclude from upstream sync.

import {Routes} from '@app/app/Routes';
import {Nagbar} from '@app/features/app/components/layout/Nagbar';
import {NagbarButton} from '@app/features/app/components/layout/NagbarButton';
import {NagbarContent} from '@app/features/app/components/layout/NagbarContent';
import {NAGBAR_TONES, NagbarToneKind} from '@app/features/app/components/layout/NagbarTones';
import {bannerText} from '@app/features/calendar/CalendarBanner';
import {dayKeyOf, eventDayKey} from '@app/features/calendar/CalendarFormat';
import {CalendarBrowserStrings as B, fill} from '@app/features/calendar/CalendarStrings';
import styles from '@app/features/calendar/components/Calendar.module.css';
import Calendar from '@app/features/calendar/state/Calendar';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {BellIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';

export const CalendarReminderNagbar = observer(({isMobile}: {isMobile: boolean}) => {
	const due = Calendar.dueReminders;
	const event = due[0];
	if (event == null) return null;
	const timezone = Calendar.timezone;
	const tone = NAGBAR_TONES[NagbarToneKind.BRAND];
	const open = (dayKey: string) => {
		Calendar.focusDay(dayKey);
		RouterUtils.transitionTo(Routes.CALENDAR);
	};
	const handleDismiss = () => Calendar.dismiss(event);
	return (
		<Nagbar
			isMobile={isMobile}
			backgroundColor={tone.backgroundColor}
			textColor={tone.textColor}
			dismissible={true}
			onDismiss={handleDismiss}
			dismissLabel={B.dismissOnThisDevice}
			data-flx="app.app-layout.nagbars.calendar-reminder-nagbar.nagbar"
		>
			<NagbarContent
				isMobile={isMobile}
				onDismiss={handleDismiss}
				dismissLabel={B.dismissOnThisDevice}
				message={
					<span className={styles.bannerMessage} data-flx="app.app-layout.nagbars.calendar-reminder-nagbar.message">
						<BellIcon
							weight="fill"
							size={14}
							aria-hidden={true}
							data-flx="app.app-layout.nagbars.calendar-reminder-nagbar.bell"
						/>
						{bannerText(event, timezone, Calendar.now)}
					</span>
				}
				actions={
					<>
						{due.length > 1 && (
							<NagbarButton
								isMobile={isMobile}
								onClick={() => open(dayKeyOf(Calendar.now, timezone))}
								data-flx="app.app-layout.nagbars.calendar-reminder-nagbar.more"
							>
								{fill(B.more, {n: due.length - 1})}
							</NagbarButton>
						)}
						<NagbarButton
							isMobile={isMobile}
							onClick={() => open(eventDayKey(event, timezone))}
							data-flx="app.app-layout.nagbars.calendar-reminder-nagbar.open"
						>
							{B.openCalendar}
						</NagbarButton>
					</>
				}
				data-flx="app.app-layout.nagbars.calendar-reminder-nagbar.nagbar-content"
			/>
		</Nagbar>
	);
});
